import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { logout } from "@/store/auth";
import { trpc } from "@/trpc/client";
import { DASHBOARD_HOME } from "@/lib/routes";
import { getEntryWorkspace } from "@/lib/workspaces";
import { isAccountDeletedError, isForbiddenError } from "@/lib/trpcErrors";
import { handleTeamLoginError } from "@/lib/authErrors";
import { EmailPasswordForm } from "@/components/auth/EmailPasswordForm";
import { pushAuthSuccessEvent, pushSignupStart } from "@/lib/gtm";
import { usePushOnce } from "@/lib/usePushOnce";

// Dedicated, crawler-hidden email/password sign-in for provisioned team members
// (#378). The server gate (`isAuthenticated`) is the boundary — this page only
// renders the form. `/login` stays Google-only.
export const Route = createFileRoute("/_home/team-login")({
  head: () => ({
    meta: [
      { title: "Team Login" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async ({ context }) => {
    if (context.auth.user) {
      let workspace;
      try {
        workspace = await getEntryWorkspace();
      } catch (err) {
        if (isAccountDeletedError(err)) {
          try {
            await logout();
          } catch {
            /* ignore */
          }
          throw redirect({ to: "/account-deleted" });
        }
        // A live Firebase session the server gate now rejects (e.g. the member
        // was de-allowlisted) throws FORBIDDEN here. Don't crash into the error
        // boundary — sign out and fall through to render the form so they can
        // sign in as an allowed member.
        if (isForbiddenError(err)) {
          try {
            await logout();
          } catch {
            /* ignore */
          }
          return;
        }
        throw err;
      }
      if (!workspace) {
        throw redirect({ to: "/" });
      }
      throw redirect({
        to: DASHBOARD_HOME,
        params: { workspaceId: workspace.workspaceId },
      });
    }
  },
  component: TeamLoginPage,
});

function TeamLoginPage() {
  const navigate = useNavigate();

  usePushOnce(pushSignupStart, "signup_start");

  const createUserMutation = useMutation({
    ...trpc.authenticatedRouterFunction.createUser.mutationOptions(),
    onSuccess: async ({ isNewUser }) => {
      pushAuthSuccessEvent({ method: "password", isNewUser });
      // The post-sign-in workspace fetch + navigation can still reject (network,
      // a de-allowlisted session → FORBIDDEN, account deleted mid-flight). Catch
      // it so it can't surface as an unhandled rejection / crash the error
      // boundary after a successful login (#378 review).
      try {
        const workspace = await getEntryWorkspace();
        if (!workspace) {
          await navigate({ to: "/" });
          return;
        }
        await navigate({
          to: DASHBOARD_HOME,
          params: { workspaceId: workspace.workspaceId },
        });
      } catch (error) {
        await handleTeamLoginError(error, navigate);
      }
    },
    onError: (error) => handleTeamLoginError(error, navigate),
  });

  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="w-full max-w-sm px-6">
        <div className="mb-8 flex justify-center">
          <h1 className="font-headline uppercase text-2xl tracking-tight text-[color:var(--gs-text)]">
            Team Login
          </h1>
        </div>

        <EmailPasswordForm
          onFirebaseUser={(email) => createUserMutation.mutate({ email })}
          onError={(message) => toast.error(message)}
          isPending={createUserMutation.isPending}
        />
      </div>
    </div>
  );
}
